export const FORMAT_DURATION_STYLE = Object.freeze({
  ELAPSED: 'elapsed',
  CLOCK: 'clock',
  MIXED_MILLISECONDS: 'mixedMilliseconds',
  RAW_MILLISECONDS: 'rawMilliseconds'
});

const toFiniteDuration = (value) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed;
};

const formatElapsedDuration = ({ value, details, invalidValue, minimumValue }) => {
  const parsed = toFiniteDuration(value);

  if (parsed === null) {
    return invalidValue;
  }

  const normalizedSeconds = Math.max(minimumValue, Math.floor(parsed));
  const timeUnits = [
    { unit: 'h', fullUnit: ' hours', seconds: 3600 },
    { unit: 'm', fullUnit: ' mins', seconds: 60 }
  ];
  let remainingSeconds = normalizedSeconds;
  const output = [];

  timeUnits.forEach(({ unit, fullUnit, seconds }) => {
    const unitValue = Math.floor(remainingSeconds / seconds);

    if (unitValue > 0) {
      output.push(`${unitValue}${details ? fullUnit : unit}`);
    }

    remainingSeconds %= seconds;
  });

  if (output.length > 0) {
    return output.join(', ');
  }

  return `${remainingSeconds}${details ? 'secs' : 's'}`;
};

const formatClockDuration = ({ value, invalidValue, padMinutes }) => {
  const parsed = toFiniteDuration(value);

  if (parsed === null) {
    return invalidValue;
  }

  const totalSeconds = Math.max(0, Math.floor(parsed));
  const minutesText = String(Math.floor(totalSeconds / 60)).padStart(padMinutes ? 2 : 1, '0');
  const secondsText = String(totalSeconds % 60).padStart(2, '0');

  return `${minutesText}:${secondsText}`;
};

const formatMixedMillisecondsDuration = ({ value, invalidValue }) => {
  const parsed = toFiniteDuration(value);

  if (parsed === null || parsed < 0) {
    return invalidValue;
  }

  if (parsed < 1000) {
    return `${Math.round(parsed)} ms`;
  }

  const seconds = parsed / 1000;

  if (seconds < 60) {
    return `${seconds.toFixed(seconds >= 10 ? 1 : 2)} s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainderSeconds = Math.round(seconds % 60);

  return `${minutes}m ${remainderSeconds}s`;
};

const formatRawMillisecondsDuration = ({ locale, value, invalidValue }) => {
  const parsed = toFiniteDuration(value);

  if (parsed === null) {
    return invalidValue;
  }

  return `${Math.max(0, Math.round(parsed)).toLocaleString(locale)} ms`;
};

export const formatDuration = (value, options = {}) => {
  const { style = FORMAT_DURATION_STYLE.ELAPSED, details = false, padMinutes = false, locale, minimumValue = style === FORMAT_DURATION_STYLE.ELAPSED ? 1 : 0, invalidValue = style === FORMAT_DURATION_STYLE.CLOCK ? (padMinutes ? '00:00' : '0:00') : style === FORMAT_DURATION_STYLE.MIXED_MILLISECONDS ? '—' : style === FORMAT_DURATION_STYLE.RAW_MILLISECONDS ? null : '' } = options;

  if (style === FORMAT_DURATION_STYLE.CLOCK) {
    return formatClockDuration({ value, invalidValue, padMinutes });
  }

  if (style === FORMAT_DURATION_STYLE.MIXED_MILLISECONDS) {
    return formatMixedMillisecondsDuration({ value, invalidValue });
  }

  if (style === FORMAT_DURATION_STYLE.RAW_MILLISECONDS) {
    return formatRawMillisecondsDuration({ locale, value, invalidValue });
  }

  return formatElapsedDuration({ value, details, invalidValue, minimumValue });
};
